import * as React from "react";
import { useState, useEffect } from "react";
import {
  Title,
  useNotify,
} from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Divider,
  alpha,
  MenuItem,
  Select,
} from "@mui/material";
import {
  People as UsersIcon,
  ShoppingCart as OrdersIcon,
  Receipt as InvoicesIcon,
  LocalOffer as InputsIcon,
  Handyman as ServicesIcon,
  Timeline as TrendsIcon,
  Equalizer as StatsIcon,
  AttachMoney as RevenueIcon,
  TrendingUp as GrowthIcon,
  Category as CategoriesIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { TextField as MuiTextField } from "@mui/material";

// Import components from the new overview directory
import {
  SummaryCard,
  SafePieChart,
  SafeBarChart,
  TopPerformersList,
  formatSDG,
} from "../../components/reports/overview";

const OverallReports = () => {
  const notify = useNotify();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0: Overview, 1: Trends, 2: Recent Activity
  const [stateTab, setStateTab] = useState(0); // State tabs (0,1,2 for three states)
  const theme = useTheme();
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [gender, setGender] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Meta data
  const [states, setStates] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  const allStatuses = [
    "Processing",
    "Delivered",
    "Cancelled",
    "Pending",
    "Paid",
    "Shipped",
  ];
  const genders = ["male", "female"];

  // Managers only see their assigned state(s). Assignments can change while a
  // manager stays logged in (an admin may add/remove states at any time), so
  // always fetch the current assignment from the backend instead of trusting
  // the localStorage snapshot taken at login; only fall back to that cache if
  // the fetch itself fails.
  useEffect(() => {
    const role = localStorage.getItem("permissions");

    if (role === "manager") {
      const fetchAssignedStates = async () => {
        try {
          const token = JSON.parse(localStorage.getItem("token"));
          const response = await fetch(
            `${process.env.REACT_APP_URL}/admin-state-assignments/me`,
            {
              method: "GET",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
              },
            }
          );
          const statesData = (await response.json()) || [];
          localStorage.setItem("states", JSON.stringify(statesData));
          setStates(statesData);
        } catch (err) {
          console.error("Error fetching assigned states:", err);
          notify("Error loading states", { type: "warning" });
          try {
            const storedStates = localStorage.getItem("states");
            setStates(storedStates ? JSON.parse(storedStates) || [] : []);
          } catch (parseErr) {
            setStates([]);
          }
        } finally {
          setLoadingMeta(false);
        }
      };
      fetchAssignedStates();
      return;
    }

    const fetchStates = async () => {
      try {
        const stateResponse = await fetch(
          `${process.env.REACT_APP_URL}/admin/state`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );
        const statesData = await stateResponse.json();
        setStates(statesData);
      } catch (err) {
        console.error("Error fetching states:", err);
        notify("Error loading states", { type: "warning" });
      } finally {
        setLoadingMeta(false);
      }
    };
    fetchStates();
  }, [notify]);

  // Fetch report data
  useEffect(() => {
    const fetchReportData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const token = JSON.parse(localStorage.getItem("token"));
        const params = new URLSearchParams();
        
        // Add filters
        if (statusFilter) params.append("status", statusFilter);
        if (gender) {
          params.append("gender", gender);
          params.append("buyerGender", gender);
          params.append("sellerGender", gender);
        }
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        
        // Add state filter based on selected state tab
        if (states.length > 0 && stateTab < states.length) {
          params.append("state", states[stateTab].id);
        }

        const url =
          `${process.env.REACT_APP_URL}/admin/reports/overall` +
          (params.toString() ? `?${params.toString()}` : "");
          
        console.log("Fetching with state:", states[stateTab]?.name, "ID:", states[stateTab]?.id);
        
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Report data for state:", states[stateTab]?.name, data);
        setReportData(data);
      } catch (err) {
        console.error("Error fetching report data:", err);
        setError(err);
        notify("Error loading overall report data", { type: "error" });
      } finally {
        setLoading(false);
      }
    };

    if (states.length > 0) {
      fetchReportData();
    }
  }, [statusFilter, gender, startDate, endDate, stateTab, states, notify]);

  if (loadingMeta || loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 3 }}>
        Failed to load overall report data. Please try again later.
        <Box mt={1} fontFamily="monospace">
          {error.message}
        </Box>
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="warning" sx={{ my: 3 }}>
        No overall report data available.
      </Alert>
    );
  }

  const {
    period = {},
    kpis = {},
    trends = {},
    distributions = {},
    topPerformers = {},
    financials = {},
    recentActivity = {},
  } = reportData;

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.default,
          0.9
        )} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        p: 3,
        borderRadius: "16px",
      }}
    >
      <Title title="Overall Reports" />
      
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight="800"
          sx={{
            background: `black`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
          }}
        >
          System Overview Dashboard
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" sx={{ mt: 1 }}>
          {period?.start ? new Date(period.start).toLocaleDateString() : 'N/A'} -{" "}
          {period?.end ? new Date(period.end).toLocaleDateString() : 'N/A'}
        </Typography>
        {states.length > 0 && stateTab < states.length && (
          <Typography variant="subtitle2" color="primary" sx={{ mt: 1, fontWeight: "bold" }}>
            Currently viewing: {states[stateTab].name_en??states[stateTab].name}
          </Typography>
        )}
      </Box>

      {/* State Tabs */}
      {states.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Paper
            elevation={0}
            sx={{
              background: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: "blur(8px)",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            <Tabs
              value={stateTab}
              onChange={(e, newValue) => {
                setStateTab(newValue);
                // Reset to first content tab when changing states
                setActiveTab(0);
              }}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{
                "& .MuiTab-root": {
                  fontWeight: "600",
                  textTransform: "none",
                  fontSize: "1rem",
                },
              }}
            >
              {states.map((state, index) => (
                // <Tab key={state.id} label={state.name} />
                <Tab key={state.id} label={state.name_en??state.name} />
              ))}
            </Tabs>
          </Paper>
        </Box>
      )}

      {/* Summary KPIs */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <SummaryCard
            title="Total Contributions"
            value={kpis?.totalInkindOrders || 0}
            icon={RevenueIcon}
            secondaryValue={`${(kpis?.inKindCompletionRate || 0).toFixed(1)}% completed`}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <SummaryCard
            title="Total Revenue"
            value={formatSDG(financials?.totalRevenue || 0)}
            icon={RevenueIcon}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <SummaryCard
            title="Total Users"
            value={
              distributions?.userTypes?.reduce(
                (sum, type) => sum + parseInt(type.count || 0),
                0
              ) || 0
            }
            icon={UsersIcon}
            secondaryValue={`${(kpis?.userActivationRate || 0).toFixed(1)}% verified`}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <SummaryCard
            title="Total Orders"
            value={kpis?.totalOrders || 0}
            icon={OrdersIcon}
            secondaryValue={`${kpis?.paidOrdersCount || 0} completed orders`}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2">Status</Typography>
          <Select
            fullWidth
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {allStatuses.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <Typography variant="subtitle2">Gender</Typography>
          <Select
            fullWidth
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="">Any</MenuItem>
            {genders.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <DatePicker
            label="From"
            value={startDate || null}
            onChange={(newDate) => {
              const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
              setStartDate(iso);
            }}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                fullWidth
                size="small"
                inputProps={{ ...params.inputProps, readOnly: true }}
              />
            )}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <DatePicker
            label="To"
            value={endDate || null}
            onChange={(newDate) => {
              const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
              setEndDate(iso);
            }}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                fullWidth
                size="small"
                inputProps={{ ...params.inputProps, readOnly: true }}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Content Tabs */}
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(8px)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                fontWeight: "600",
                textTransform: "none",
                fontSize: "0.875rem",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Trends" />
            <Tab label="Recent Activity" />
          </Tabs>
        </Paper>
      </Box>

      {/* Overview Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SafePieChart
              data={distributions?.userTypes || []}
              title="User Types Distribution"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SafePieChart
              data={distributions?.offerTypes || []}
              title="Crop Types Distribution"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SafePieChart
              data={distributions?.serviceTypes || []}
              title="Service Types Distribution"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SafePieChart
              data={distributions?.inputTypes || []}
              title="Input Types Distribution"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SafePieChart
              data={distributions?.invoiceStatuses || []}
              title="Invoice Status"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SafePieChart
              data={distributions?.orderStatuses || []}
              title="Order Status Distribution"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SafeBarChart
              data={financials?.revenueByType || []}
              title="Revenue by Product Type"
              xKey="type"
              yKey="totalRevenue"
            />
          </Grid>
        </Grid>
      )}

      {/* Trends Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SafeBarChart
              data={trends?.users || []}
              title="User Growth"
              xKey="month"
              yKey="count"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SafeBarChart
              data={trends?.orders || []}
              title="Order Trends"
              xKey="month"
              yKey="count"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SafeBarChart
              data={trends?.revenue || []}
              title="Revenue Trends"
              xKey="month"
              yKey="value"
            />
          </Grid>
        </Grid>
      )}

      {/* Recent Activity Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TopPerformersList
              title="Top Buyers"
              data={topPerformers?.buyers || []}
              icon={UsersIcon}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TopPerformersList
              title="Top Sellers"
              data={topPerformers?.sellers || []}
              icon={OrdersIcon}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                background: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: "blur(8px)",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  sx={{ mb: 2 }}
                >
                  Recent Users
                </Typography>
                {recentActivity?.newUsers?.length > 0 ? (
                  <List sx={{ py: 0 }}>
                    {recentActivity.newUsers.map((user, index) => (
                      <React.Fragment key={user.id}>
                        <ListItem sx={{ px: 0, py: 1 }}>
                          <ListItemText
                            primary={user.name || user.phonenumber}
                            secondary={`Joined: ${new Date(
                              user.created_at
                            ).toLocaleDateString()}`}
                          />
                        </ListItem>
                        {index < recentActivity.newUsers.length - 1 && (
                          <Divider />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Alert severity="info">No recent users</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                background: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: "blur(8px)",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  sx={{ mb: 2 }}
                >
                  Recent Invoices
                </Typography>
                {recentActivity?.recentInvoices?.length > 0 ? (
                  <List sx={{ py: 0 }}>
                    {recentActivity.recentInvoices.map((invoice, index) => (
                      <React.Fragment key={invoice.id}>
                        <ListItem sx={{ px: 0, py: 1 }}>
                          <ListItemText
                            primary={formatSDG(invoice.amount)}
                            secondary={`Due: ${new Date(
                              invoice.dueDate
                            ).toLocaleDateString()}`}
                          />
                          <Typography variant="body2" color="textSecondary">
                            {invoice.status}
                          </Typography>
                        </ListItem>
                        {index < recentActivity.recentInvoices.length - 1 && (
                          <Divider />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Alert severity="info">No recent invoices</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default OverallReports;